import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { SiteNav } from "../components/SiteNav";
import { ForgetMeNotIcon } from "../components/ForgetMeNotIcon";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function NewProfile() {
    const navigate = useNavigate();
    const [isCouple, setIsCouple] = useState(false);
    const [form, setForm] = useState({
        subject_name: "",
        subject_name_2: "",
        birth_year: "",
        birth_place: "",
        relationship: "",
        bio: "",
    });
    const [busy, setBusy] = useState(false);

    function update(field, value) {
        setForm((f) => ({ ...f, [field]: value }));
    }

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = {
                subject_name: form.subject_name.trim(),
                subject_name_2: isCouple ? form.subject_name_2.trim() : null,
                birth_year: form.birth_year ? Number(form.birth_year) : null,
                birth_place: form.birth_place.trim() || null,
                relationship: form.relationship.trim() || null,
                bio: form.bio.trim() || null,
            };
            const { data } = await api.post("/profiles", payload);
            toast.success(`${data.subject_name}'s remembr has begun.`);
            navigate(`/profiles/${data.id}`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create. Try again.");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="min-h-screen bg-[#FDFBF7]" data-testid="new-profile-page">
            <SiteNav />
            <main className="pt-28 pb-24 px-6 rem-watercolor-bg min-h-screen">
                <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl mx-auto">
                    <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#5C5A59] hover:text-[#6B7DB8] mb-8" data-testid="new-back-dashboard">
                        <ArrowLeft size={16} /> Back to dashboard
                    </Link>

                    <div className="flex justify-center mb-6"><ForgetMeNotIcon size={48} /></div>
                    <p className="text-center font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-4">A new remembr</p>
                    <h1 className="text-center font-serif text-3xl sm:text-4xl text-[#2D2B2A] leading-tight" data-testid="new-profile-heading">
                        Who would you like to <em className="italic text-[#4A5A95]">reminisce</em> with?
                    </h1>
                    <p className="mt-4 text-center text-[#5C5A59] leading-relaxed">
                        Start with their name. Everything else can be filled in later, together with the family.
                    </p>

                    <form onSubmit={submit} className="mt-10 rem-greeting-card rounded-[2rem] p-8 sm:p-10 space-y-4" data-testid="new-profile-form">
                        {/* Single vs couple */}
                        <div className="flex gap-2 mb-2">
                            <button
                                type="button"
                                onClick={() => setIsCouple(false)}
                                className={`flex-1 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${!isCouple ? "bg-[#6B7DB8] text-white" : "bg-white text-[#5C5A59] border border-[#E5E1D8]"}`}
                                data-testid="profile-type-single"
                            >
                                For one
                            </button>
                            <button
                                type="button"
                                onClick={() => setIsCouple(true)}
                                className={`flex-1 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${isCouple ? "bg-[#6B7DB8] text-white" : "bg-white text-[#5C5A59] border border-[#E5E1D8]"}`}
                                data-testid="profile-type-couple"
                            >
                                A couple — together
                            </button>
                        </div>

                        <Field label="Their name" testid="np-subject-name">
                            <input required value={form.subject_name} onChange={(e) => update("subject_name", e.target.value)} placeholder="e.g. John Tilly" className={inputCls} data-testid="np-subject-name-input" />
                        </Field>

                        {isCouple && (
                            <Field label="And their partner's name" testid="np-subject-name-2">
                                <input required value={form.subject_name_2} onChange={(e) => update("subject_name_2", e.target.value)} placeholder="e.g. Margaret" className={inputCls} data-testid="np-subject-name-2-input" />
                            </Field>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Birth year (optional)" testid="np-birth-year">
                                <input type="number" min={1900} max={2100} value={form.birth_year} onChange={(e) => update("birth_year", e.target.value)} placeholder="1945" className={inputCls} data-testid="np-birth-year-input" />
                            </Field>
                            <Field label="Birthplace (optional)" testid="np-birth-place">
                                <input value={form.birth_place} onChange={(e) => update("birth_place", e.target.value)} placeholder="Carlton, Melbourne" className={inputCls} data-testid="np-birth-place-input" />
                            </Field>
                        </div>

                        <Field label="How are you related? (optional)" testid="np-relationship">
                            <input value={form.relationship} onChange={(e) => update("relationship", e.target.value)} placeholder="My dad / My grandmother / My husband" className={inputCls} data-testid="np-relationship-input" />
                        </Field>

                        <Field label="A line about them (optional — you can edit later)" testid="np-bio">
                            <textarea rows={3} value={form.bio} onChange={(e) => update("bio", e.target.value)} placeholder="The funniest man on Sydney Road. Loved his Holdens, his Cherry Ripes, and his footy." className={`${inputCls} rounded-2xl py-3`} data-testid="np-bio-input" />
                        </Field>

                        <button type="submit" disabled={busy} className="rem-start-button w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white font-medium text-sm transition-all disabled:opacity-60 mt-3" data-testid="np-submit">
                            {busy ? "Creating…" : "Begin their remembr"}
                            <ArrowRight weight="bold" size={16} />
                        </button>
                    </form>
                </motion.div>
            </main>
        </div>
    );
}

const inputCls = "w-full px-5 py-3 rounded-full text-sm bg-white text-[#2D2B2A] placeholder-[#8A8786] border border-[#E5E1D8] focus:border-[#8C9ECB] outline-none transition-all";

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-[0.22em] text-[#6B7DB8] font-semibold mb-2">{label}</span>
            {children}
        </label>
    );
}
